// Global admin defaults + the future role/permission model.
//
// Values are fetched from the backend (`GET /api/admin`) so every client sees
// the same ping timings. Saving requires the design/admin token
// (`x-admin-token`, same secret as the theme publisher). Until user accounts
// land the current role is a local preview (`?role=…` or localStorage); the
// capability table is the contract the server-side auth will enforce later.

import { api } from "./base";

/** Privilege levels. Higher level = more access. */
export type Role = "spectator" | "player" | "tester" | "mod" | "admin";

export const ROLE_LEVEL: Record<Role, number> = {
  spectator: 0,
  player: 1,
  tester: 2,
  mod: 3,
  admin: 4,
};

export interface RoleInfo {
  id: Role;
  label: string;
  summary: string;
  capabilities: string[];
}

/** Preview matrix; the server will enforce the same capabilities later. */
export const ROLE_INFO: RoleInfo[] = [
  {
    id: "spectator",
    label: "Spectator",
    summary: "Watch only; no match decisions.",
    capabilities: ["watch", "free camera", "chat (read)", "self ping"],
  },
  {
    id: "player",
    label: "Player",
    summary: "Match choices that don't change the outcome (timeout, votes).",
    capabilities: ["ready / unready", "vote map", "call timeout", "chat", "ping"],
  },
  {
    id: "tester",
    label: "Tester",
    summary: "Early UI/settings access without match moderation.",
    capabilities: ["all player rights", "tester settings", "experimental UI options"],
  },
  {
    id: "mod",
    label: "Moderator",
    summary: "Mid-match decisions and moderation.",
    capabilities: [
      "all player rights",
      "kick / mute",
      "pause / unpause",
      "restart round",
      "change map",
      "balance teams",
    ],
  },
  {
    id: "admin",
    label: "Admin",
    summary: "Everything, including global defaults and accounts.",
    capabilities: [
      "all moderator rights",
      "global admin defaults",
      "ruleset / mode control",
      "accounts + roles",
      "server configuration",
    ],
  },
];

export function canRole(role: Role, required: Role): boolean {
  return ROLE_LEVEL[role] >= ROLE_LEVEL[required];
}

const ROLE_KEY = "webrace.role";
const ADMIN_TOKEN_KEY = "webrace.theme.admin"; // shared with /design publishing
const ADMIN_CACHE_KEY = "webrace.admin.cache";

/** Global admin defaults (ping marker timings for now). */
export interface AdminSettings {
  /** How long the active ping stays fully visible before its end fade. */
  pingTtlMs: number;
  /** Fade-out time when a newer ping replaces the old one. */
  pingFadeMs: number;
  /** Fade-out time at the end of the active ping's lifetime. */
  pingEndFadeMs: number;
  /** Ignore pings fired faster than this (ms). */
  pingCooldownMs: number;
}

export const DEFAULT_ADMIN: AdminSettings = {
  pingTtlMs: 6000,
  pingFadeMs: 500,
  pingEndFadeMs: 1000,
  pingCooldownMs: 200,
};

function clampInt(v: unknown, def: number, lo: number, hi: number): number {
  const n = Number(v);
  return Number.isFinite(n) ? Math.min(hi, Math.max(lo, Math.round(n))) : def;
}

export function normalizeAdmin(raw: unknown): AdminSettings {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return {
    pingTtlMs: clampInt(o.pingTtlMs, DEFAULT_ADMIN.pingTtlMs, 500, 60000),
    pingFadeMs: clampInt(o.pingFadeMs, DEFAULT_ADMIN.pingFadeMs, 0, 5000),
    pingEndFadeMs: clampInt(o.pingEndFadeMs, DEFAULT_ADMIN.pingEndFadeMs, 0, 5000),
    pingCooldownMs: clampInt(o.pingCooldownMs, DEFAULT_ADMIN.pingCooldownMs, 0, 5000),
  };
}

export function loadCachedAdmin(): AdminSettings {
  try {
    const raw = localStorage.getItem(ADMIN_CACHE_KEY);
    return raw ? normalizeAdmin(JSON.parse(raw)) : { ...DEFAULT_ADMIN };
  } catch {
    return { ...DEFAULT_ADMIN };
  }
}

function cacheAdmin(s: AdminSettings): void {
  try {
    localStorage.setItem(ADMIN_CACHE_KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

/** Fetch the published global admin defaults (null when unreachable). */
export async function fetchAdminSettings(): Promise<AdminSettings | null> {
  try {
    const r = await fetch(api("/api/admin"), { cache: "no-store" });
    if (!r.ok) return null;
    const data = (await r.json()) as { settings?: unknown };
    const s = normalizeAdmin(data?.settings);
    cacheAdmin(s);
    return s;
  } catch {
    return null;
  }
}

export function loadAdminToken(): string {
  try {
    return localStorage.getItem(ADMIN_TOKEN_KEY) ?? "";
  } catch {
    return "";
  }
}

export function saveAdminToken(token: string): void {
  try {
    localStorage.setItem(ADMIN_TOKEN_KEY, token);
  } catch {
    /* ignore */
  }
}

/** Publish global admin defaults. The server checks the token. */
export async function saveAdminSettings(
  settings: AdminSettings,
  token: string,
): Promise<{ ok: boolean; error?: string }> {
  if (!token) return { ok: false, error: "enter the admin token" };
  try {
    const r = await fetch(api("/api/admin"), {
      method: "PUT",
      headers: { "Content-Type": "application/json", "x-admin-token": token },
      body: JSON.stringify({ settings: normalizeAdmin(settings) }),
    });
    if (!r.ok) {
      const data = (await r.json().catch(() => ({}))) as { error?: string };
      return { ok: false, error: data.error ?? `save failed (${r.status})` };
    }
    cacheAdmin(normalizeAdmin(settings));
    saveAdminToken(token);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: String((e as Error)?.message ?? e) };
  }
}

function validRole(v: unknown): v is Role {
  return (
    v === "spectator" ||
    v === "player" ||
    v === "tester" ||
    v === "mod" ||
    v === "admin"
  );
}

/** Current role: `?role=` > localStorage > admin (no accounts yet). */
export function loadPreviewRole(): Role {
  try {
    const q = new URLSearchParams(location.search).get("role");
    if (validRole(q)) {
      localStorage.setItem(ROLE_KEY, q);
      return q;
    }
    const saved = localStorage.getItem(ROLE_KEY);
    if (validRole(saved)) return saved;
  } catch {
    /* ignore */
  }
  return "admin";
}

export function savePreviewRole(role: Role): void {
  try {
    localStorage.setItem(ROLE_KEY, role);
  } catch {
    /* ignore */
  }
}
